import React from "react";
import { DateTime } from "luxon";
import { TCasesDayViewBulkAwaitForm, TCasesDayViewBulkAwaitMode, TCasesDayViewBulkRescheduleForm, TCasesDayViewBulkRescheduleMode } from "../../../../../../domain/codecs/form/CasesDayViewWeekCountsForm";
import { CRMColors } from "../../../../models/CRMColors";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../../models/CRMFontSizes";
import { Background } from "../../../BuildingBlocks/Background";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { Text } from "../../../BuildingBlocks/Text";
import { CRMInfoBox } from "../../../Simple/CRMInfoBox/CRMInfoBox";
import { CRMParagraph } from "../../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { CRMThinColoredButton } from "../../../Simple/CRMThinColoredButton/CRMThinColoredButton";
import { WeightSemiBold } from "../../../WeightSemiBold/WeightSemiBold";
import { useOpenClose } from "../../../../hooks/UseOpenClose";
import { Relative } from "../../../BuildingBlocks/Relative";
import { Absolute } from "../../../BuildingBlocks/Absolute";
import { CRMDeferPopout } from "../../../CrmDeferPopout/CRMDeferPopout";

type TCRMDayOverdueBannerProps = {
    reschedulableTasksCount: number;
    keyDatesCount: number;
    roadblocksCount: number;
    assignedTo: string | null;
    sailHomesListingOnly: boolean;
    bulkRescheduleForm: TCasesDayViewBulkRescheduleForm;
    onReviewOverdue: () => void;
    onReschedule: (mode: TCasesDayViewBulkRescheduleMode) => void;
    bulkAwaitForm: TCasesDayViewBulkAwaitForm;
    onBulkAwait: (mode: TCasesDayViewBulkAwaitMode) => void;
    onUndoBulkAwait: () => void;
    onUndo: () => void;
};

const londonToday = (): DateTime =>
    DateTime.utc().setZone("Europe/London").startOf("day");

const MenuOption = (props: {
    label: string;
    onClick: () => void;
}): JSX.Element => (
    <Background
        padding={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}
        cursor="pointer"
        onClick={props.onClick}
    >
        <Text size={CRMFontSizes.SMALL} weight={600} color={CRMColors.NEUTRAL_INK}>
            {props.label}
        </Text>
    </Background>
);

export const CRMDayOverdueBanner = (props: TCRMDayOverdueBannerProps): JSX.Element => {
    const openCloseMenu = useOpenClose();
    const openCloseCalendar = useOpenClose();

    const overdueTotal = props.reschedulableTasksCount + props.keyDatesCount + props.roadblocksCount;
    const needActionCount = props.keyDatesCount + props.roadblocksCount;

    const openCloseAwait = useOpenClose();

    const isAwaiting = props.bulkAwaitForm.status === "submitting";
    const awaitedCount = props.bulkAwaitForm.children.affected_roadblock_count;
    const isShowingAwaitUndo =
        props.bulkAwaitForm.status === "success"
        && awaitedCount > 0
        && props.bulkAwaitForm.edited.mode.type !== "undo";

    const isRescheduling = props.bulkRescheduleForm.status === "submitting";
    const movedCount = props.bulkRescheduleForm.children.updated.length;
    const isShowingUndo =
        props.bulkRescheduleForm.status === "success"
        && movedCount > 0
        && props.bulkRescheduleForm.edited.mode.type !== "explicit";

    const onPickMode = (mode: TCasesDayViewBulkRescheduleMode) => {
        openCloseMenu.setIsOpen(false);
        openCloseCalendar.setIsOpen(false);
        props.onReschedule(mode);
    };

    const onPickAwaitMode = (mode: TCasesDayViewBulkAwaitMode) => {
        openCloseAwait.setIsOpen(false);
        props.onBulkAwait(mode);
    };

    const onPickDate = (date: string | null) => {
        if (date !== null) {
            onPickMode({ type: "to_date", assigned_to: props.assignedTo, date, sail_homes_listing_only: props.sailHomesListingOnly });
        }
    };

    if (overdueTotal === 0 && !isShowingUndo && !isShowingAwaitUndo) {
        return <></>;
    }

    return (
        <Background padding={`0px ${CRMSpacing.X_LARGE}`}>
            <CRMInfoBox
                color={isShowingUndo || isShowingAwaitUndo ? CRMColors.HIGHLIGHTS_POSITIVE_GREEN_12 : CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_12}
                border={`1px solid ${isShowingUndo || isShowingAwaitUndo ? CRMColors.HIGHLIGHTS_POSITIVE_GREEN_10 : CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_8}`}
            >
                {/* UNDO STATE — text left, action right, one row (each state owns its
                    own SpacingRow: a conditional fragment would count as a single grid
                    column and stack the action underneath the text) */}
                {isShowingUndo &&
                    <SpacingRow
                        childSize="1fr max-content"
                        alignItems="center"
                        spacing={CRMSpacing.MEDIUM}
                    >
                        <CRMParagraph>
                            {movedCount === 1
                                ? <><WeightBold>Moved 1 task</WeightBold> to a new date.</>
                                : <><WeightBold>Moved {movedCount} tasks</WeightBold> to new dates.</>
                            }
                        </CRMParagraph>
                        <CRMThinColoredButton
                            backgroundColor={CRMColors.NEUTRAL_2}
                            fontColor={CRMColors.NEUTRAL_12}
                            label={<WeightSemiBold>Undo</WeightSemiBold>}
                            onClick={props.onUndo}
                        />
                    </SpacingRow>
                }

                {/* DEFERRED-ROADBLOCKS UNDO STATE — its own row for the same reason as
                    the one above: a conditional fragment collapses the grid to one column. */}
                {isShowingAwaitUndo && !isShowingUndo &&
                    <SpacingRow
                        childSize="1fr max-content"
                        alignItems="center"
                        spacing={CRMSpacing.MEDIUM}
                    >
                        <CRMParagraph>
                            {awaitedCount === 1
                                ? <><WeightBold>Parked 1 roadblock</WeightBold>.</>
                                : <><WeightBold>Parked {awaitedCount} roadblocks</WeightBold>.</>
                            }
                        </CRMParagraph>
                        <CRMThinColoredButton
                            backgroundColor={CRMColors.NEUTRAL_2}
                            fontColor={CRMColors.NEUTRAL_12}
                            label={<WeightSemiBold>Undo</WeightSemiBold>}
                            onClick={props.onUndoBulkAwait}
                        />
                    </SpacingRow>
                }

                {/* OVERDUE STATE */}
                {!isShowingUndo && !isShowingAwaitUndo &&
                    <SpacingRow
                        childSize="1fr max-content"
                        alignItems="center"
                        spacing={CRMSpacing.MEDIUM}
                    >
                            <CRMParagraph>
                                You have <WeightBold>{overdueTotal} overdue</WeightBold>
                                {overdueTotal === 1 ? " item" : " items"}
                                {" — "}
                                {props.reschedulableTasksCount > 0 &&
                                    <><WeightBold>{props.reschedulableTasksCount} {props.reschedulableTasksCount === 1 ? "task" : "tasks"} can be rescheduled</WeightBold></>
                                }
                                {props.reschedulableTasksCount > 0 && needActionCount > 0 && ", "}
                                {needActionCount > 0 &&
                                    <><WeightBold>{needActionCount} {needActionCount === 1 ? "needs" : "need"} action</WeightBold></>
                                }.
                            </CRMParagraph>

                            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                <CRMThinColoredButton
                                    backgroundColor={CRMColors.NEUTRAL_PAPER}
                                    fontColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}
                                    label={<WeightSemiBold>Review overdue</WeightSemiBold>}
                                    onClick={props.onReviewOverdue}
                                />

                                {props.roadblocksCount > 0 &&
                                    <Relative divRef={openCloseAwait.ref}>
                                        <CRMThinColoredButton
                                            backgroundColor={CRMColors.NEUTRAL_PAPER}
                                            fontColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}
                                            label={<WeightSemiBold>
                                                {isAwaiting
                                                    ? "Parking..."
                                                    : `Park ${props.roadblocksCount} ${props.roadblocksCount === 1 ? "roadblock" : "roadblocks"}`}
                                            </WeightSemiBold>}
                                            onClick={isAwaiting ? () => undefined : openCloseAwait.toggleOpen}
                                            icon="down-arrow"
                                        />

                                        {openCloseAwait.isOpen &&
                                            <Absolute top="34px" right="0px" zIndex={10}>
                                                <Background
                                                    color={CRMColors.NEUTRAL_PAPER}
                                                    borderDefinition={`1px solid ${CRMColors.NEUTRAL_8}`}
                                                    borderRadius="8px"
                                                    width="220px"
                                                >
                                                    <SpacingColumn>
                                                        <MenuOption
                                                            label="Spread across this week"
                                                            onClick={() => onPickAwaitMode({
                                                                type: "spread",
                                                                assigned_to: props.assignedTo,
                                                                sail_homes_listing_only: props.sailHomesListingOnly,
                                                            })}
                                                        />
                                                        <MenuOption
                                                            label="Tomorrow"
                                                            onClick={() => onPickAwaitMode({
                                                                type: "to_date",
                                                                assigned_to: props.assignedTo,
                                                                sail_homes_listing_only: props.sailHomesListingOnly,
                                                                date: londonToday().plus({ days: 1 }).toISODate(),
                                                            })}
                                                        />
                                                        <MenuOption
                                                            label="Next Monday"
                                                            onClick={() => onPickAwaitMode({
                                                                type: "to_date",
                                                                assigned_to: props.assignedTo,
                                                                sail_homes_listing_only: props.sailHomesListingOnly,
                                                                date: londonToday().startOf("week").plus({ weeks: 1 }).toISODate(),
                                                            })}
                                                        />
                                                    </SpacingColumn>
                                                </Background>
                                            </Absolute>
                                        }
                                    </Relative>
                                }

                                {props.reschedulableTasksCount > 0 &&
                                    <Relative divRef={openCloseMenu.ref}>
                                        <CRMThinColoredButton
                                            backgroundColor={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}
                                            fontColor={CRMColors.NEUTRAL_PAPER}
                                            label={<WeightSemiBold>
                                                {isRescheduling
                                                    ? "Rescheduling..."
                                                    : `Reschedule ${props.reschedulableTasksCount} ${props.reschedulableTasksCount === 1 ? "task" : "tasks"}`}
                                            </WeightSemiBold>}
                                            onClick={isRescheduling ? () => undefined : openCloseMenu.toggleOpen}
                                            icon="down-arrow"
                                        />

                                        {openCloseMenu.isOpen &&
                                            <Absolute top="34px" right="0px" zIndex={10}>
                                                <Background
                                                    color={CRMColors.NEUTRAL_PAPER}
                                                    borderDefinition={`1px solid ${CRMColors.NEUTRAL_8}`}
                                                    borderRadius="8px"
                                                    width="220px"
                                                >
                                                    <SpacingColumn>
                                                        <MenuOption
                                                            label="Spread across this week"
                                                            onClick={() => onPickMode({ type: "spread", assigned_to: props.assignedTo, sail_homes_listing_only: props.sailHomesListingOnly })}
                                                        />
                                                        <MenuOption
                                                            label="Tomorrow"
                                                            onClick={() => onPickMode({
                                                                type: "to_date",
                                                                assigned_to: props.assignedTo,
                                                                date: londonToday().plus({ days: 1 }).toISODate(),
                                                                sail_homes_listing_only: props.sailHomesListingOnly,
                                                            })}
                                                        />
                                                        <MenuOption
                                                            label="Next Monday"
                                                            onClick={() => onPickMode({
                                                                type: "to_date",
                                                                assigned_to: props.assignedTo,
                                                                date: londonToday().startOf("week").plus({ weeks: 1 }).toISODate(),
                                                                sail_homes_listing_only: props.sailHomesListingOnly,
                                                            })}
                                                        />
                                                        <MenuOption
                                                            label="Pick a date..."
                                                            onClick={() => {
                                                                openCloseMenu.setIsOpen(false);
                                                                openCloseCalendar.setIsOpen(true);
                                                            }}
                                                        />
                                                    </SpacingColumn>
                                                </Background>
                                            </Absolute>
                                        }
                                    </Relative>
                                }

                                {openCloseCalendar.isOpen &&
                                    <Relative divRef={openCloseCalendar.ref}>
                                        <Absolute top="34px" right="0px" zIndex={10}>
                                            <CRMDeferPopout
                                                options="only_date"
                                                onChange={onPickDate}
                                            />
                                        </Absolute>
                                    </Relative>
                                }
                            </SpacingRow>
                    </SpacingRow>
                }
            </CRMInfoBox>
        </Background>
    );
};
